<script>
import MetricaAnalysisAPI from 'dashboard/api/metrica/analysis';
import { useMetricaAiConfig } from 'dashboard/composables/useMetricaAiConfig';
import { useAdmin } from 'dashboard/composables/useAdmin';

const DIMENSION_KEYS = [
  'rapport',
  'discovery',
  'objection_handling',
  'closing_technique',
  'communication',
  'proactiveness',
];

export default {
  name: 'MetricaAnalysisPanel',
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { loaded: aiConfigLoaded, capabilities: aiCapabilities } =
      useMetricaAiConfig();
    const { isAdmin } = useAdmin();
    return { aiConfigLoaded, aiCapabilities, isAdmin };
  },
  data() {
    return {
      loading: false,
      error: false,
      restricted: false,
      analysis: null,
      pollTimer: null,
      showAllDimensions: false,
    };
  },
  computed: {
    aiUnavailable() {
      return this.aiConfigLoaded && !this.aiCapabilities.chat;
    },
    analyzed() {
      return Boolean(this.analysis && this.analysis.analyzed);
    },
    score() {
      return this.analysis?.score ?? null;
    },
    displayScore() {
      return this.score === null ? '—' : this.score;
    },
    // The score's band is named in WORDS, not colour. ~8% of men have a colour
    // vision deficiency, and a hue alone never tells you where 63 sits on a
    // scale you cannot see — so the label carries the meaning and the segmented
    // meter carries the magnitude.
    scoreBandLabel() {
      if (this.score === null) return '';
      if (this.score >= 75) return this.$t('METRICA_ANALYSIS.BAND.GREAT');
      if (this.score >= 50) return this.$t('METRICA_ANALYSIS.BAND.GOOD');
      if (this.score >= 25) return this.$t('METRICA_ANALYSIS.BAND.FAIR');
      return this.$t('METRICA_ANALYSIS.BAND.WEAK');
    },
    // 5 segments, not a continuous bar: an LLM's judgement has nowhere near
    // 1-point resolution, so a bar filled to exactly 63% claims a precision the
    // underlying number does not have.
    scoreSegments() {
      if (this.score === null) return 0;
      return Math.round(this.score / 20);
    },
    outcomeLabel() {
      const outcome = this.analysis?.outcome;
      if (!outcome) return '';
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      return this.$t(`METRICA_ANALYSIS.OUTCOME.${outcome}`);
    },
    dimensions() {
      const scores = this.analysis?.dimension_scores || {};
      return DIMENSION_KEYS.filter(key => scores[key] !== undefined).map(
        key => ({
          key,
          // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
          label: this.$t(`METRICA_ANALYSIS.DIMENSIONS.${key}`),
          value: scores[key],
        })
      );
    },
    // Six equal rows say "read all of this"; the two weakest say "fix this".
    // The full set stays one click away — this is an exception about what to
    // surface first, not about what to keep.
    visibleDimensions() {
      if (this.showAllDimensions) return this.dimensions;
      return [...this.dimensions].sort((a, b) => a.value - b.value).slice(0, 2);
    },
    hasHiddenDimensions() {
      return this.dimensions.length > 2;
    },
    vendorAnalysis() {
      return this.analysis?.details?.vendorAnalysis || {};
    },
    strengths() {
      return this.vendorAnalysis.strengths || [];
    },
    improvements() {
      return this.vendorAnalysis.improvements || [];
    },
  },
  watch: {
    conversationId() {
      this.fetchAnalysis();
    },
  },
  mounted() {
    this.fetchAnalysis();
    // The analysis is re-run asynchronously (debounced ~2 min) after new messages,
    // so poll quietly to pick up the fresh result without a page switch.
    this.pollTimer = setInterval(
      () => this.fetchAnalysis({ silent: true }),
      30000
    );
  },
  beforeUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
  methods: {
    // -11 rather than -9: these meters are 6px tall, and the -9 step is too dim
    // to read at that size against the dark panel.
    barColor(value) {
      if (value >= 75) return 'bg-n-teal-11';
      if (value >= 50) return 'bg-n-amber-11';
      return 'bg-n-ruby-11';
    },
    filledSegments(value) {
      return Math.round(value / 20);
    },
    dimDesc(key) {
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      const k = `METRICA_REPORTS.DIMENSION_DESC.${key}`;
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      return this.$te(k) ? this.$t(k) : '';
    },
    // Translates an enum value (sentiment/phase/risk) to PT, falling back to the
    // raw value for legacy analyses that may still hold a non-mapped string.
    enumLabel(group, value) {
      if (!value) return '';
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      const key = `METRICA_ANALYSIS.${group}.${value}`;
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      return this.$te(key) ? this.$t(key) : value;
    },
    // `silent` (background poll): refresh the data without the loading flash, and
    // keep the last good state on error instead of blanking the panel.
    async fetchAnalysis(opts = {}) {
      if (!this.conversationId) return;
      const silent = opts.silent === true;
      if (!silent) {
        this.loading = true;
        this.error = false;
        this.restricted = false;
      }
      try {
        const { data } = await MetricaAnalysisAPI.getForConversation(
          this.conversationId
        );
        this.analysis = data;
        if (silent) {
          this.error = false;
          this.restricted = false;
        }
      } catch (e) {
        if (silent) return; // keep the current analysis on background polls
        // 403 = a non-admin agent viewing a conversation that isn't theirs.
        if (e.response?.status === 403) {
          this.restricted = true;
        } else {
          this.error = true;
        }
        this.analysis = null;
      } finally {
        if (!silent) this.loading = false;
      }
    },
  },
};
</script>

<template>
  <div class="px-4 py-3 text-sm text-n-slate-12">
    <!-- [metrica] Every state below was text-n-slate-10 = 3.61:1 on this
         panel's surface — under the 4.5:1 floor. All moved to slate-11. -->
    <div v-if="loading" class="flex flex-col gap-2 py-2">
      <div
        class="h-3 rounded w-2/5 bg-n-alpha-2 animate-pulse motion-reduce:animate-none"
      />
      <div
        class="h-2 rounded w-full bg-n-alpha-2 animate-pulse motion-reduce:animate-none"
      />
      <div
        class="h-2 rounded w-3/4 bg-n-alpha-2 animate-pulse motion-reduce:animate-none"
      />
      <span class="sr-only">{{ $t('METRICA_ANALYSIS.ANALYZING') }}</span>
    </div>

    <div v-else-if="restricted" class="py-2 text-n-slate-11">
      {{ $t('METRICA_ANALYSIS.RESTRICTED') }}
    </div>

    <div v-else-if="error" class="flex flex-col items-start gap-2 py-2">
      <span class="text-n-slate-11">{{ $t('METRICA_ANALYSIS.ERROR') }}</span>
      <button
        type="button"
        class="text-xs font-medium underline text-n-slate-12 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-n-brand"
        @click="fetchAnalysis()"
      >
        {{ $t('METRICA_ANALYSIS.RETRY') }}
      </button>
    </div>

    <div v-else-if="!analyzed && aiUnavailable" class="py-2 text-n-slate-11">
      {{ $t('METRICA_ANALYSIS.AI_NOT_CONFIGURED') }}
      <router-link
        v-if="isAdmin"
        :to="{ name: 'metrica_ai_settings' }"
        class="underline hover:no-underline"
      >
        {{ $t('METRICA_ANALYSIS.AI_NOT_CONFIGURED_LINK') }}
      </router-link>
    </div>

    <div v-else-if="!analyzed" class="py-2 text-n-slate-11">
      {{ $t('METRICA_ANALYSIS.NOT_ANALYZED') }}
    </div>

    <div v-else class="flex flex-col gap-3">
      <!-- Nothing here used to say the judgement was automated. -->
      <div class="flex items-center gap-1 text-xs text-n-slate-11">
        <span class="i-lucide-sparkles size-3 shrink-0" />
        {{ $t('METRICA_ANALYSIS.AI_ATTRIBUTION') }}
      </div>

      <!-- Score: a plain number with a named band and a 5-step meter, instead
           of the 3xl coloured hero it used to be. -->
      <div class="flex flex-col gap-1.5">
        <div class="flex items-baseline justify-between gap-2">
          <div class="flex items-baseline gap-1 min-w-0">
            <span class="text-base font-semibold tabular-nums text-n-slate-12">
              {{ displayScore }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ $t('METRICA_ANALYSIS.SCORE_SUFFIX') }}
            </span>
            <span
              v-if="scoreBandLabel"
              class="text-xs truncate text-n-slate-11"
            >
              · {{ scoreBandLabel }}
            </span>
          </div>
          <span
            class="px-2 py-0.5 text-xs font-medium rounded-md shrink-0 bg-n-alpha-2 text-n-slate-12"
          >
            {{ outcomeLabel }}
          </span>
        </div>
        <div v-if="score !== null" class="flex gap-px h-1.5" aria-hidden="true">
          <span
            v-for="seg in 5"
            :key="`score-seg-${seg}`"
            class="flex-1 rounded-[1px]"
            :class="seg <= scoreSegments ? barColor(score) : 'bg-n-alpha-2'"
          />
        </div>
      </div>

      <div
        v-if="analysis.exclude_from_score"
        class="text-xs italic text-n-slate-11"
      >
        {{ $t('METRICA_ANALYSIS.OUT_OF_SCORE') }}
      </div>

      <!-- Three identical pills carried three unrelated facts and read as
           tags you could act on. One plain line, and only a HIGH risk keeps a
           chip — because that is the one worth interrupting for. -->
      <div
        class="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-n-slate-11"
      >
        <span v-if="analysis.sentiment">
          {{ enumLabel('SENTIMENT', analysis.sentiment) }}
        </span>
        <span v-if="analysis.sentiment && analysis.phase">·</span>
        <span v-if="analysis.phase">
          {{ enumLabel('PHASE', analysis.phase) }}
        </span>
        <template v-if="analysis.deal_risk">
          <span v-if="analysis.sentiment || analysis.phase">·</span>
          <span
            v-if="analysis.deal_risk === 'high'"
            class="px-1.5 py-0.5 rounded bg-n-amber-3 text-n-amber-12"
          >
            {{ $t('METRICA_ANALYSIS.RISK') }}
            {{ enumLabel('RISK_LEVEL', analysis.deal_risk) }}
          </span>
          <span v-else>
            {{ $t('METRICA_ANALYSIS.RISK') }}
            {{ enumLabel('RISK_LEVEL', analysis.deal_risk) }}
          </span>
        </template>
      </div>

      <!-- The next step is what the agent acts on, so it comes before the
           retrospective detail — and reads as a sentence, not a 1-item list. -->
      <div v-if="analysis.next_action">
        <h4 class="mb-1 text-xs font-semibold text-n-slate-11">
          {{ $t('METRICA_ANALYSIS.NEXT_ACTION') }}
        </h4>
        <p class="text-xs text-n-slate-12">{{ analysis.next_action }}</p>
      </div>

      <div v-if="visibleDimensions.length" class="flex flex-col gap-1">
        <h4 class="mb-0.5 text-xs font-semibold text-n-slate-11">
          {{
            showAllDimensions
              ? $t('METRICA_ANALYSIS.SUMMARY')
              : $t('METRICA_ANALYSIS.ATTENTION_POINTS')
          }}
        </h4>
        <div
          v-for="dim in visibleDimensions"
          :key="dim.key"
          class="flex items-center h-5 gap-2"
        >
          <span class="inline-flex items-center gap-1 shrink-0 w-24">
            <span
              v-if="dimDesc(dim.key)"
              v-tooltip="dimDesc(dim.key)"
              class="i-lucide-info size-3 text-n-slate-10 cursor-help shrink-0"
            />
            <span class="text-xs truncate text-n-slate-11">{{
              dim.label
            }}</span>
          </span>
          <div class="flex flex-1 gap-px h-1.5" aria-hidden="true">
            <span
              v-for="seg in 5"
              :key="`${dim.key}-${seg}`"
              class="flex-1 rounded-[1px]"
              :class="
                seg <= filledSegments(dim.value)
                  ? barColor(dim.value)
                  : 'bg-n-alpha-2'
              "
            />
          </div>
          <span class="w-6 text-xs font-medium text-right tabular-nums">
            {{ dim.value }}
          </span>
        </div>
        <button
          v-if="hasHiddenDimensions"
          type="button"
          class="self-start mt-0.5 text-xs underline text-n-slate-11 hover:text-n-slate-12 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-n-brand"
          @click="showAllDimensions = !showAllDimensions"
        >
          {{
            showAllDimensions
              ? $t('METRICA_ANALYSIS.SHOW_FEWER_DIMENSIONS')
              : $t('METRICA_ANALYSIS.SHOW_ALL_DIMENSIONS')
          }}
        </button>
      </div>

      <div v-if="analysis.summary">
        <h4 class="mb-1 text-xs font-semibold text-n-slate-11">
          {{ $t('METRICA_ANALYSIS.SUMMARY') }}
        </h4>
        <p class="text-xs text-n-slate-11">
          {{ analysis.summary }}
        </p>
      </div>

      <!-- Four different heading accents (slate/teal/amber/brand) in a 320px
           column was four competing signals. One heading colour; the content
           carries the difference. -->
      <div v-if="strengths.length">
        <h4 class="mb-1 text-xs font-semibold text-n-slate-11">
          {{ $t('METRICA_ANALYSIS.STRENGTHS') }}
        </h4>
        <ul class="text-xs list-disc list-inside text-n-slate-11">
          <li v-for="(item, i) in strengths" :key="`s-${i}`">{{ item }}</li>
        </ul>
      </div>

      <div v-if="improvements.length">
        <h4 class="mb-1 text-xs font-semibold text-n-slate-11">
          {{ $t('METRICA_ANALYSIS.IMPROVEMENTS') }}
        </h4>
        <ul class="text-xs list-disc list-inside text-n-slate-11">
          <li v-for="(item, i) in improvements" :key="`i-${i}`">{{ item }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>
